/**
 * Opening an acknowledgment cycle — §7.7.
 *
 * The form asks two questions and refuses to ask a third.
 *
 * **Who**, as a rule rather than a roster. §7.7 allows a role or a capability
 * held on a space, and nothing else: audience resolves dynamically so a new hire
 * enrols into a cycle opened before they existed. There is deliberately no
 * "pick some people" option here, because that is the shape the spec rules out
 * and the one everybody reaches for first.
 *
 * **By when**, optionally. A cycle with no due date is a standing obligation
 * nothing will ever chase, which is sometimes right and always worth choosing on
 * purpose.
 *
 * The third question — *which version* — is not asked and cannot be. The cycle
 * is opened against whatever readers currently resolve to: a request against an
 * older version would ask people to sign something no reader can reach, and one
 * against the working draft would ask them to sign a text still being written.
 *
 * Opening is `document.publish` rather than `document.edit`, which is the same
 * line archiving draws. It does not change what a document says; it obliges a
 * set of people to read it, and a contractor who can oblige the whole company to
 * read something has been handed an organisational lever by an editing
 * capability. The server enforces that; this form only decides whether the
 * button is worth drawing.
 */

import { useState } from 'react';
import { useOpenAcknowledgmentCycle, useSpaces } from '../../api/queries.ts';
import type { AcknowledgmentAudienceKind, OpenCycleResponse, UserRole } from '../../api/types.ts';
import { USER_ROLES } from '../../api/types.ts';
import { CAPABILITY_OPTIONS } from '../../components/capabilities.ts';
import { roleLabel } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';

/** `dueDateSchema` in `acknowledgments/routes.ts`, mirrored so a typo is not a 400. */
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

const AUDIENCE_OPTIONS: ReadonlyArray<{
  value: AcknowledgmentAudienceKind;
  label: string;
  description: string;
}> = [
  {
    value: 'role',
    label: 'Everyone with a role',
    description: 'Resolved from `person.role` every time the cycle is read.',
  },
  {
    value: 'space_grant',
    label: 'Everyone holding a capability on a space',
    description: 'Resolved through the same precedence as every other access answer.',
  },
];

const ROLE_OPTIONS = USER_ROLES.map((role) => ({ value: role, label: roleLabel(role) }));

function addDays(days: number, from = new Date()): string {
  const date = new Date(from.getFullYear(), from.getMonth(), from.getDate() + days);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export interface OpenAcknowledgmentDialogProps {
  nodeId: string;
  title: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onOpened?: (result: OpenCycleResponse) => void;
}

export function OpenAcknowledgmentDialog({
  nodeId,
  title,
  isOpen,
  onOpenChange,
  onOpened,
}: OpenAcknowledgmentDialogProps) {
  return (
    <Dialog
      title="Ask for acknowledgment"
      description={title}
      size="lg"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {(close) => (
        <OpenCycleForm nodeId={nodeId} onClose={close} {...(onOpened ? { onOpened } : {})} />
      )}
    </Dialog>
  );
}

function OpenCycleForm({
  nodeId,
  onClose,
  onOpened,
}: {
  nodeId: string;
  onClose: () => void;
  onOpened?: (result: OpenCycleResponse) => void;
}) {
  const open = useOpenAcknowledgmentCycle(nodeId);
  const spaces = useSpaces();

  const [kind, setKind] = useState<AcknowledgmentAudienceKind>('role');
  const [role, setRole] = useState<UserRole>('staff');
  const [spaceId, setSpaceId] = useState('');
  const [capability, setCapability] = useState('document.view');
  const [dueDate, setDueDate] = useState('');

  const spaceOptions = (spaces.data?.spaces ?? []).map((space) => ({
    value: space.id,
    label: space.name,
    description: space.kind === 'client' ? 'Client space' : 'Internal space',
  }));

  const dueWellFormed = dueDate.trim() === '' || CALENDAR_DATE.test(dueDate.trim());
  const ready = kind === 'role' ? true : spaceId !== '' && capability !== '';

  return (
    <>
      <DialogBody>
        <Alert tone="info" title="This is scoped to the version readers see right now">
          <div className="mt-1 flex flex-col gap-2">
            <p>
              Acknowledgment attaches to a version, not to a document — otherwise &quot;everyone
              acknowledged the expense policy&quot; would stay true after a rewrite. Publishing a
              revision opens a new cycle, and these signatures will not carry into it.
            </p>
            <p>
              The audience is a question the system re-asks on every read, never a list captured
              now. Somebody provisioned next week is enrolled without anyone touching this request.
            </p>
          </div>
        </Alert>

        <Select
          label="Who has to read it"
          options={AUDIENCE_OPTIONS}
          selectedKey={kind}
          onSelectionChange={setKind}
        />

        {kind === 'role' ? (
          <Select
            label="Role"
            options={ROLE_OPTIONS}
            selectedKey={role}
            onSelectionChange={setRole}
            description="Whoever holds this role at the moment the cycle is read — not the people who hold it today."
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Space"
              options={spaceOptions}
              selectedKey={spaceId === '' ? null : spaceId}
              onSelectionChange={setSpaceId}
              placeholder={spaces.isPending ? 'Loading…' : 'Choose a space'}
              isRequired
            />
            <Select
              label="Capability held there"
              options={CAPABILITY_OPTIONS}
              selectedKey={capability}
              onSelectionChange={setCapability}
              isRequired
              description="There is no membership edge for a space — holding a grant is what membership means here."
            />
          </div>
        )}

        <TextField
          label="Due date"
          value={dueDate}
          onChange={setDueDate}
          placeholder="YYYY-MM-DD"
          description="Optional. A calendar day, as the column stores it. Without one, nothing will ever chase this."
          {...(dueWellFormed ? {} : { errorMessage: 'Use YYYY-MM-DD, or leave it empty.' })}
        />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onPress={() => setDueDate(addDays(7))}>
            A week
          </Button>
          <Button size="sm" variant="secondary" onPress={() => setDueDate(addDays(30))}>
            30 days
          </Button>
          <Button size="sm" variant="tertiary" onPress={() => setDueDate('')}>
            No date
          </Button>
        </div>

        {spaces.isError ? <ErrorState error={spaces.error} /> : null}
        {open.isError ? <ErrorState error={open.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={open.isPending}
          isDisabled={!ready || !dueWellFormed}
          onPress={() =>
            open.mutate(
              {
                audience:
                  kind === 'role'
                    ? { kind: 'role', role }
                    : { kind: 'space_grant', spaceId, capability },
                ...(dueDate.trim() ? { dueDate: dueDate.trim() } : {}),
              },
              {
                onSuccess: (result) => {
                  onOpened?.(result);
                  onClose();
                },
              },
            )
          }
        >
          Open the cycle
        </Button>
      </DialogFooter>
    </>
  );
}
